import { CSSObject } from '@emotion/react';
import {
    createResponsiveStyles,
    HoneycombTheme,
    ResponsiveStyleValue,
    ScoreColor,
    ScoreElementColors,
    ScoreSize,
    useHoneycombTheme,
} from '../../theming';
import { isScoreThemeableColor } from '../../utils';

function getSizeStyles(theme: HoneycombTheme) {
    return (size: ScoreSize) => {
        const componentTheme = theme.components.themes.Score;
        const sizeProps = componentTheme.size[size];

        return {
            width: sizeProps.width,
            height: sizeProps.height,
            borderRadius: sizeProps.radius,
            fontSize: sizeProps.fontSize,
            padding: sizeProps.padding,
        };
    };
}

const getElementColors = (color: ScoreColor, theme: HoneycombTheme): ScoreElementColors => {
    const { components } = theme;
    const componentTheme = components.themes.Score;
    if (isScoreThemeableColor(color)) {
        return componentTheme.colors[color];
    }
    return componentTheme.fixedColors[color];
};

const getColorStyles = (colors: ScoreElementColors, theme: HoneycombTheme): CSSObject => {
    const { background, text } = theme.fn.resolveFlatColorObject(colors);
    return {
        backgroundColor: background,
        color: text,
    };
};

export interface ScoreStylesParams {
    color: ScoreColor;
    size: ResponsiveStyleValue<ScoreSize>;
}

type ScoreStyleClasses = 'root';

export function useStyles({ color, size }: ScoreStylesParams): {
    styles: { [key in ScoreStyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();

    const root: CSSObject = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        ...createResponsiveStyles(
            [
                {
                    value: size,
                    getStyles: getSizeStyles(theme),
                },
            ],
            theme
        ),
        ...getColorStyles(getElementColors(color, theme), theme),
    };

    return {
        styles: {
            root,
        },
    };
}
